/**
 * Canonical app-wide date/time formatting — Australian conventions.
 *
 * SINGLE SOURCE OF TRUTH. Do not call `date-fns` `format`, `toLocaleDateString`,
 * or hand-roll date strings elsewhere — import a helper from here. This keeps
 * every surface consistent (day-first, dd/MM/yyyy) and makes a format change a
 * one-file edit.
 *
 * Written form uses an ordinal day: "Saturday, 9th January 2027".
 * Numeric form is dd/MM/yyyy: "09/01/2027".
 *
 * INVALID-DATE SAFETY: date-fns `format`/`formatDistanceToNow` THROW a
 * RangeError on an invalid Date. Several call sites format raw, unvalidated
 * fields (e.g. `event.date`, `event.start_at`), and the previous
 * `toLocaleDateString` path degraded to "Invalid Date" rather than throwing.
 * So every helper here returns '' (or a safe default) for an invalid input
 * instead of throwing — a bad date must never white-screen a list.
 */
import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isSameDay,
  isValid,
} from 'date-fns';

export type DateInput = Date | string | number;

function toDate(d: DateInput): Date {
  return d instanceof Date ? d : new Date(d);
}

/** Non-throwing format — '' for an invalid date. All string helpers route here. */
function fmt(d: DateInput, token: string): string {
  const dt = toDate(d);
  return isValid(dt) ? format(dt, token) : '';
}

/** The canonical format tokens. Referenced by the helpers below — the only
 *  place format strings live. */
export const DATE_FORMATS = {
  /** 09/01/2027 */
  numeric: 'dd/MM/yyyy',
  /** Saturday, 9th January 2027 */
  written: 'EEEE, do MMMM yyyy',
  /** 9 Jan 2027 */
  short: 'd MMM yyyy',
  /** 3:00 PM */
  time: 'h:mm a',
} as const;

/** 09/01/2027 */
export function formatDateNumeric(d: DateInput): string {
  return fmt(d, DATE_FORMATS.numeric);
}

/** Saturday, 9th January 2027 */
export function formatDateWritten(d: DateInput): string {
  return fmt(d, DATE_FORMATS.written);
}

/** 9 Jan 2027 */
export function formatDateShort(d: DateInput): string {
  return fmt(d, DATE_FORMATS.short);
}

/** Sat, 9 Jan — compact weekday + day + month (no year), day-first. */
export function formatDateShortDay(d: DateInput): string {
  return fmt(d, 'EEE, d MMM');
}

/** 9 January 2027 — written, day-first, no weekday/ordinal (date separators). */
export function formatDateWrittenShort(d: DateInput): string {
  return fmt(d, 'd MMMM yyyy');
}

/** Sat — abbreviated weekday only. */
export function formatWeekdayShort(d: DateInput): string {
  return fmt(d, 'EEE');
}

/** 9 Jan — day + abbreviated month, no year, day-first. */
export function formatDayMonth(d: DateInput): string {
  return fmt(d, 'd MMM');
}

/** Jan 2027 — abbreviated month + year. */
export function formatMonthYear(d: DateInput): string {
  return fmt(d, 'MMM yyyy');
}

/** January 2027 — full month name + year (section headings). */
export function formatMonthYearLong(d: DateInput): string {
  return fmt(d, 'MMMM yyyy');
}

/**
 * 'YYYY-MM' → 'MMM yyyy', TZ-safe. Built from LOCAL date parts (`new Date(y, m-1, 1)`),
 * never a UTC-parsed date-only ISO — `new Date('2026-05-01')` is UTC midnight, which prints
 * the previous month in any UTC− timezone. Use this for period (reporting-month) labels.
 */
export function formatPeriodMonthYear(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return formatMonthYear(new Date(y, m - 1, 1));
}

/** 'YYYY-MM' → 'MMMM yyyy'. Same TZ-safe construction as {@link formatPeriodMonthYear}. */
export function formatPeriodMonthYearLong(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return formatMonthYearLong(new Date(y, m - 1, 1));
}

/**
 * A reporting period rendered as the span it actually covers — the finance
 * cockpit header, which used to print the raw `2026-10` key beside the word
 * "Overview" and told a builder nothing about what the figures underneath span.
 *
 *   current month   → 'Month to date · 1 – 3 Oct'   (or '1 Oct' on the first)
 *   any other month → '1 – 31 Aug 2026'
 *   malformed key   → the key, unchanged
 *
 * "To date" rather than "so far", deliberately: the cockpit mixes figures that
 * accumulate over the period (gross margin, net margin) with closing balances
 * that are merely as-at it (debtor days, cash runway). "So far" would claim the
 * balances were running totals. "To date" is true of both.
 *
 * `period` is matched STRICTLY. The loose `!y || !m` guard used by the two
 * helpers above accepts '2026-13' and '2026-10-01', which would then render as
 * some other month entirely; here they are handed back untouched.
 *
 * TZ-safe by construction, per {@link formatPeriodMonthYear}: every Date is
 * built from local integer parts and the period string is never parsed. This
 * cannot be covered by a unit test — Node fixes TZ at process start and CI runs
 * UTC, where the broken and correct constructions agree — so it is a rule for
 * the reader, not an assertion.
 */
export function periodSpanLabel(period: string, today: DateInput): string {
  const parts = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (!parts) return period;

  const now = toDate(today);
  if (!isValid(now)) return period;

  const year = Number(parts[1]);
  const month = Number(parts[2]); // 1-12
  const dayOf = (day: number) => formatDayMonth(new Date(year, month - 1, day));

  if (year === now.getFullYear() && month === now.getMonth() + 1) {
    const day = now.getDate();
    return day === 1
      ? `Month to date · ${dayOf(1)}`
      : `Month to date · 1 – ${dayOf(day)}`;
  }

  // Day 0 of the following month is the last day of this one — leap years and
  // 30-day months included, never a hard-coded 31.
  const lastDay = new Date(year, month, 0).getDate();
  return `1 – ${dayOf(lastDay)} ${year}`;
}

/**
 * What a Xero report's figures cover, said truthfully. Xero dates a monthly column at the month's
 * END, so a sync on 11 Oct is labelled "as at 31 Oct" — a date that has not happened. When the
 * figures were fetched before that date they are the month so far:
 *
 *   fetched after the as-at date → 'as at 31 Oct 2026'
 *   fetched before it            → 'for October so far, to 11 Oct'
 *   no fetch time / bad dates    → 'as at …' (what Xero printed)
 */
export function figuresCoverLabel(asAt: DateInput, fetchedAt: DateInput | null | undefined): string {
  const end = toDate(asAt);
  const got = fetchedAt == null ? null : toDate(fetchedAt);
  if (!isValid(end) || !got || !isValid(got) || got.getTime() >= end.getTime()) {
    return `as at ${formatDateShort(asAt)}`;
  }
  return `for ${fmt(end, 'MMMM')} so far, to ${formatDayMonth(got)}`;
}

/** 09/01/2027, 3:00 PM — numeric date + time. */
export function formatDateTimeNumeric(d: DateInput): string {
  return fmt(d, 'dd/MM/yyyy, h:mm a');
}

/** 9 Jan, 3:00 PM — compact day + month + time (no year), day-first. */
export function formatDateTimeShort(d: DateInput): string {
  return fmt(d, 'd MMM, h:mm a');
}

/** 3:00:45 PM — time with seconds (execution/debug logs). */
export function formatTimeSeconds(d: DateInput): string {
  return fmt(d, 'h:mm:ss a');
}

/** 3:00 PM */
export function formatTime(d: DateInput): string {
  return fmt(d, DATE_FORMATS.time);
}

/** 3:00 AM – 3:00 PM */
export function formatTimeRange(start: DateInput, end: DateInput): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

/** Saturday, 9th January 2027 · 3:00 PM */
export function formatDateTimeWritten(d: DateInput): string {
  return `${formatDateWritten(d)} · ${formatTime(d)}`;
}

/**
 * Compact, ordinal-free date range (matches the event hero style):
 *   same day         → "Saturday, 9th January 2027"
 *   same month/year  → "9 – 17 January 2027"
 *   same year        → "9 January – 2 February 2027"
 *   different year   → "9 January 2027 – 2 February 2028"
 */
export function formatDateRange(start: DateInput, end: DateInput): string {
  const s = toDate(start);
  const e = toDate(end);
  if (!isValid(s) || !isValid(e)) return '';
  if (isSameDay(s, e)) return format(s, DATE_FORMATS.written);
  if (format(s, 'MMMM yyyy') === format(e, 'MMMM yyyy')) {
    return `${format(s, 'd')} – ${format(e, 'd MMMM yyyy')}`;
  }
  if (format(s, 'yyyy') === format(e, 'yyyy')) {
    return `${format(s, 'd MMMM')} – ${format(e, 'd MMMM yyyy')}`;
  }
  return `${format(s, 'd MMMM yyyy')} – ${format(e, 'd MMMM yyyy')}`;
}

/** True when the event spans more than one calendar day. Invalid → false. */
export function isMultiDay(start: DateInput, end: DateInput): boolean {
  const s = toDate(start);
  const e = toDate(end);
  if (!isValid(s) || !isValid(e)) return false;
  return differenceInCalendarDays(e, s) >= 1;
}

/** "in 6 months" / "3 days ago". Invalid → ''. */
export function formatRelativeToNow(d: DateInput): string {
  const dt = toDate(d);
  return isValid(dt) ? formatDistanceToNow(dt, { addSuffix: true }) : '';
}

/**
 * 2027-01-09 — sortable ISO date KEY, not a display string. Use for grouping /
 * Map keys / comparisons where a stable locale-independent day identity is
 * needed (NOT for anything a user reads).
 */
export function toDateKey(d: DateInput): string {
  return fmt(d, 'yyyy-MM-dd');
}

// ---- business-timezone calendar days -----------------------------------------

/**
 * YYYY-MM-DD in Australia/Sydney. A machine calendar key, not a display
 * string - Intl is used only to read the business-timezone day. (Not a banned
 * formatter: `.format` on an Intl.DateTimeFormat, not `.toLocaleDateString`.)
 */
export function sydneyYmd(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(d);
}

/**
 * The Australia/Sydney calendar day of an instant, short style ("9 Jan 2027").
 * Business-day windows (state episodes, resume days) are Sydney days -
 * formatting the raw instant in the viewer's timezone shows the previous day
 * anywhere west of Sydney.
 */
export function formatSydneyDay(d: DateInput): string {
  return formatDateShort(`${sydneyYmd(new Date(d))}T00:00:00`);
}

/**
 * The instant Australia/Sydney midnight falls on for a YYYY-MM-DD calendar day.
 *
 * Sydney runs at UTC+11 (AEDT) or UTC+10 (AEST), so its midnight is 13:00 or
 * 14:00 UTC the day before; whichever candidate reads back as the same Sydney
 * day is the right one. Serialising a chosen day at BROWSER-local midnight
 * instead stored the wrong day for anyone west of Sydney.
 */
export function sydneyMidnightIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utcMidnight = Date.UTC(y, m - 1, d);
  for (const offsetHours of [11, 10]) {
    const candidate = new Date(utcMidnight - offsetHours * 3_600_000);
    if (sydneyYmd(candidate) === ymd) return candidate.toISOString();
  }
  // Unreachable while Sydney stays on +10/+11 - fall back to standard time.
  return new Date(utcMidnight - 10 * 3_600_000).toISOString();
}

/** Add whole days to a YYYY-MM-DD calendar key. */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const yyyy = String(dt.getUTCFullYear());
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Last calendar day of access (Sydney) from an exclusive `removal_due_at` /
 * `status_until` instant: the day before the day the removal lands.
 */
export function lastAccessYmdFromDueAt(dueAt: string): string {
  return addDaysYmd(sydneyYmd(new Date(dueAt)), -1);
}
